// ============================================================
// Token studente: passaggio TechyWeb (parent) -> app incorporata (iframe) via postMessage (WP-13e)
// ============================================================
// Stesso canale di TL4K_REQUEST_AUTH / TL4K_AUTH_CREDENTIALS, con regole piu' strette perche' qui il
// dato e' di un minore:
//  - il parent risponde SOLO a una finestra figlia sua (`source.parent === window`) e con
//    targetOrigin = l'origine reale del messaggio, mai '*'; origini non TL4K o 'null' (sandbox) no;
//  - il figlio accetta SOLO dal suo parent e solo da un'origine TL4K (o da `trustedOrigins`);
//  - il parent manda solo un token di tipo `participant` (mai la `proposal`);
//  - il token non passa per l'URL, quindi non finisce in cronologia, titoli finestra, stato salvato
//    dei processi ne' nel Referer. (Il frammento `#tl4k_st` di studentToken.ts resta come alternativa.)
//  - parent che non risponde (TechyWeb vecchio) = il figlio prosegue senza token dopo il timeout,
//    cioe' come oggi.
// ============================================================

import { getStudentToken, getStudentTokenKind, setStudentToken } from "./studentToken";
import { hostOfOrigin, isUntrustedContentHost, parseTrustedOrigins, warnOnce } from "./originRules";
import { COLLAB_API_BASE } from "./authService";
import { loadRuntimeTrustedOrigins } from "./trustedOriginsRuntime";

export const TL4K_REQUEST_STUDENT_TOKEN = "TL4K_REQUEST_STUDENT_TOKEN" as const;
export const TL4K_STUDENT_TOKEN = "TL4K_STUDENT_TOKEN" as const;

export type Tl4kStudentTokenMessage = { type: typeof TL4K_STUDENT_TOKEN; token: string | null };

const TL4K_HOSTS = ["techlab4kids.info"];
const LOCAL_HOSTS = ["localhost", "127.0.0.1"];

// WP-13f: l'elenco delle origini fidate si legge anche A RUNTIME per ambiente (trustedOriginsRuntime), cosi'
// la stessa immagine funziona dai container locali, da quelli di staging e dall'IP del server. I divieti
// (host di contenuti di terzi) restano qui nel codice e valgono anche per le voci dell'elenco.
export { parseTrustedOrigins };

export interface TrustOptions {
  /** Ammette http(s)://localhost e 127.0.0.1. Solo sviluppo: in produzione resta false. */
  allowLocalhost?: boolean;
}

/**
 * True per origini di app TL4K: https su *.techlab4kids.info (esclusi gli host di contenuti di terzi come
 * storage.*) e, solo con `allowLocalhost`, localhost/127.0.0.1.
 */
export function isTl4kOrigin(origin: string, options?: TrustOptions): boolean {
  try {
    const u = new URL(origin);
    const host = u.hostname.toLowerCase();
    if (LOCAL_HOSTS.includes(host)) {
      return options?.allowLocalhost === true && (u.protocol === "http:" || u.protocol === "https:");
    }
    if (u.protocol !== "https:") return false;
    if (isUntrustedContentHost(host)) return false;
    return TL4K_HOSTS.some((h) => host.endsWith(`.${h}`));
  } catch {
    return false;
  }
}


export function isTl4kRequestStudentTokenMessage(data: unknown): boolean {
  return typeof data === "object" && data !== null && (data as { type?: unknown }).type === TL4K_REQUEST_STUDENT_TOKEN;
}

export function isTl4kStudentTokenMessage(data: unknown): data is Tl4kStudentTokenMessage {
  if (typeof data !== "object" || data === null) return false;
  const d = data as { type?: unknown; token?: unknown };
  return d.type === TL4K_STUDENT_TOKEN && (d.token === null || typeof d.token === "string");
}

export interface ParentResponderOptions {
  /**
   * Origini delle app incorporate a cui si puo' rispondere (match esatto, es. VITE_TECHYWEB_TRUSTED_IFRAME_ORIGINS).
   * Elenco vuoto = nessuna risposta (protettivo): in produzione l'elenco e' obbligatorio.
   */
  allowedOrigins?: string[];
  /** Solo sviluppo: risponde anche a localhost/127.0.0.1. */
  allowLocalhost?: boolean;
  /**
   * La finestra e' una di quelle che il parent ha aperto come app TL4K (es. l'iframe di EmbeddedAppWindow)?
   * Altri iframe diretti (HTML di catalogo, anteprime) non ricevono mai il token, anche se la loro origine
   * fosse nell'elenco. Se assente, vale solo il controllo dell'origine.
   */
  isAllowedSource?: (source: Window) => boolean;
  /**
   * WP-13f: origini delle app incorporate lette A RUNTIME per ambiente (di norma
   * `loadRuntimeTrustedOrigins(...).appOrigins`). Si SOMMANO a `allowedOrigins` (compatibilita' con
   * VITE_TECHYWEB_TRUSTED_IFRAME_ORIGINS bakato nel build). Una promessa che fallisce o un elenco vuoto
   * non aggiungono nulla: chiuso per difetto. Si interroga solo per origini non gia' ammesse in modo
   * statico, e mai per origini vietate ('null', storage.*, ...).
   */
  runtimeOrigins?: () => Promise<string[]>;
}

/**
 * PARENT (TechyWeb): risponde alle richieste delle app incorporate. Una sola volta per pagina.
 * Ritorna la funzione per smettere.
 *
 * Ordine dei controlli: tipo del messaggio, finestra figlia diretta, divieti (origini non http(s),
 * 'null', host di contenuti di terzi), poi l'origine nell'elenco (statico, localhost in sviluppo, oppure
 * elenco letto a runtime). Solo dopo si guarda se la finestra e' un'app aperta da TechyWeb.
 */
export function installStudentTokenParentResponder(options?: ParentResponderOptions): () => void {
  if (typeof window === "undefined") return () => {};
  const allowed = parseTrustedOrigins(options?.allowedOrigins);
  // L'elenco di runtime si legge in modo pigro, solo quando arriva una richiesta da un'origine non ammessa in
  // modo statico: in produzione normale nessuna GET in piu'.

  const respond = (source: Window, origin: string) => {
    if (options?.isAllowedSource && !options.isAllowedSource(source)) return;
    // Solo `participant`: la `proposal` serve all'ingresso di TechyWeb e non si passa a nessuno.
    const token = getStudentTokenKind() === "participant" ? getStudentToken() : null;
    try {
      source.postMessage({ type: TL4K_STUDENT_TOKEN, token } satisfies Tl4kStudentTokenMessage, origin);
    } catch {
      // finestra chiusa nel frattempo
    }
  };

  const handler = (event: MessageEvent) => {
    if (!isTl4kRequestStudentTokenMessage(event.data)) return;
    const source = event.source as Window | null;
    if (!source || source === window) return;
    try {
      if (source.parent !== window) return; // solo finestre figlie dirette
    } catch {
      return;
    }
    const origin = event.origin;
    // Divieti nel codice: valgono anche se l'origine e' in un elenco. 'null' (sandbox) non e' http(s).
    if (parseTrustedOrigins([origin]).length === 0) return;

    const staticOk =
      allowed.includes(origin) ||
      (options?.allowLocalhost === true && isTl4kOrigin(origin, { allowLocalhost: true }) && LOCAL_HOSTS.includes(hostOfOrigin(origin)));
    if (staticOk) {
      respond(source, origin);
      return;
    }
    if (!options?.runtimeOrigins) {
      if (allowed.length === 0) warnOnce("parent-no-origins", "[studentToken] nessuna origine fidata configurata: il token non viene inviato a nessuna app");
      return;
    }
    options
      .runtimeOrigins()
      .then((list) => {
        const runtime = parseTrustedOrigins(list);
        if (runtime.length === 0 && allowed.length === 0) {
          warnOnce("parent-no-origins", "[studentToken] nessuna origine fidata configurata: il token non viene inviato a nessuna app");
          return;
        }
        if (runtime.includes(origin)) respond(source, origin);
      })
      .catch(() => {
        warnOnce("parent-no-origins", "[studentToken] elenco origini fidate non disponibile: il token non viene inviato");
      });
  };
  window.addEventListener("message", handler);
  return () => window.removeEventListener("message", handler);
}

export interface RequestStudentTokenOptions {
  /** Attesa massima della risposta del parent. Default 1000 ms. */
  timeoutMs?: number;
  /** Origini ammesse oltre a *.techlab4kids.info (es. un parent di collaudo). Match esatto. */
  trustedOrigins?: string[];
  /** Solo sviluppo: accetta anche un parent su localhost. */
  allowLocalhost?: boolean;
  /**
   * WP-13f: origini di TechyWeb ammesse, lette A RUNTIME dal backend dell'app (`parentOrigins`). Default:
   * l'elenco di COLLAB_API_BASE. `null` lo disattiva. Si interroga solo quando il parent NON e' gia'
   * ammesso in modo statico (in produzione normale non parte nessuna richiesta in piu') e mai per
   * origini vietate. Non raggiungibile / vuoto / malformato = nessuna origine aggiunta (chiuso per difetto).
   */
  runtimeOrigins?: (() => Promise<string[]>) | null;
  /** Attesa massima per leggere l'elenco a runtime, dopo la risposta del parent. Default 1500 ms. */
  runtimeTimeoutMs?: number;
}

function defaultRuntimeParentOrigins(): Promise<string[]> {
  return loadRuntimeTrustedOrigins({ apiBase: COLLAB_API_BASE }).then((r) => r?.parentOrigins ?? []);
}

/** Origine del parent dichiarata da TechyWeb nell'URL dell'iframe (`tl4k_collab_origin`), se presente e valida. */
function declaredParentOrigin(): string | null {
  try {
    const raw = new URLSearchParams(window.location.search).get("tl4k_collab_origin");
    return raw ? new URL(raw).origin : null;
  } catch {
    return null;
  }
}

/**
 * FIGLIO (app incorporata): chiede il token al parent e lo salva. Ritorna true se ne e' arrivato uno.
 * Fuori da un iframe o senza risposta entro il timeout: false, e l'app prosegue come prima.
 * Il token si accetta solo dal parent diretto e solo da un'origine attendibile (non storage.*): TL4K
 * (*.techlab4kids.info), `trustedOrigins`, oppure l'elenco di runtime dell'ambiente; se l'URL dichiara
 * `tl4k_collab_origin` deve coincidere. `tl4k_collab_origin` NON rende attendibile nessuno da solo: il
 * parent deve comunque stare in un elenco che l'app riceve dal proprio backend, non dall'URL. Cosi' un
 * sito che incornicia l'app non puo' imporle il proprio token (fissazione dell'identita').
 */
export function requestStudentTokenFromParent(options?: RequestStudentTokenOptions): Promise<boolean> {
  if (typeof window === "undefined") return Promise.resolve(false);
  let parent: Window;
  try {
    parent = window.parent;
  } catch {
    return Promise.resolve(false);
  }
  if (parent === window) return Promise.resolve(false);
  const timeoutMs = options?.timeoutMs ?? 1000;
  const runtimeTimeoutMs = options?.runtimeTimeoutMs ?? 1500;
  const trusted = parseTrustedOrigins(options?.trustedOrigins);
  const declared = declaredParentOrigin();
  const runtimeOrigins = options?.runtimeOrigins === null ? null : (options?.runtimeOrigins ?? defaultRuntimeParentOrigins);

  return new Promise<boolean>((resolve) => {
    const startedAt = Date.now();
    let done = false;
    let timer = 0;
    const finish = (ok: boolean) => {
      if (done) return;
      done = true;
      window.removeEventListener("message", onMessage);
      window.clearTimeout(timer);
      resolve(ok);
    };
    const accept = (event: MessageEvent, origin: string) => {
      if (declared && declared !== origin) {
        console.warn("[studentToken] token ignorato: origine non attendibile", origin);
        return;
      }
      const token = (event.data as Tl4kStudentTokenMessage).token;
      finish(token ? setStudentToken(token) : false);
    };
    const onMessage = (event: MessageEvent) => {
      if (event.source !== parent) return;
      if (!isTl4kStudentTokenMessage(event.data)) return;
      const origin = event.origin;
      // Divieti nel codice: 'null', schemi non http(s), storage.* ecc. non passano mai, nemmeno da un elenco.
      const bannedOrAbsurd = parseTrustedOrigins([origin]).length === 0;
      const staticOk = !bannedOrAbsurd && (trusted.includes(origin) || isTl4kOrigin(origin, { allowLocalhost: options?.allowLocalhost }));
      if (staticOk) {
        accept(event, origin);
        return;
      }
      if (bannedOrAbsurd || !runtimeOrigins) {
        console.warn("[studentToken] token ignorato: origine non attendibile", origin);
        return;
      }
      // Origine non TL4K: si guarda l'elenco dell'ambiente. L'attesa del parent e' finita, ne parte una nuova
      // (breve) per la rete; un parent che risponde ma non e' in elenco = nessun token.
      window.clearTimeout(timer);
      timer = window.setTimeout(() => finish(false), runtimeTimeoutMs);
      // Verdetto negativo: si torna ad aspettare solo quanto restava dell'attesa originale.
      const resumeWait = () => {
        if (done) return;
        window.clearTimeout(timer);
        timer = window.setTimeout(() => finish(false), Math.max(0, startedAt + timeoutMs - Date.now()));
      };
      runtimeOrigins()
        .then((list) => {
          if (done) return;
          if (parseTrustedOrigins(list).includes(origin)) {
            accept(event, origin);
          } else {
            console.warn("[studentToken] token ignorato: origine non attendibile", origin);
            resumeWait();
          }
        })
        .catch(() => {
          console.warn("[studentToken] token ignorato: elenco origini fidate non disponibile");
          resumeWait();
        });
    };
    timer = window.setTimeout(() => finish(false), timeoutMs);
    window.addEventListener("message", onMessage);
    try {
      // targetOrigin '*' e' inevitabile qui (il figlio non conosce l'origine del parent), ma il messaggio
      // di richiesta non contiene nulla: il segreto viaggia solo nella risposta, con origine esplicita.
      parent.postMessage({ type: TL4K_REQUEST_STUDENT_TOKEN }, "*");
    } catch {
      finish(false);
    }
  });
}
